import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { translate } from 'react-i18next';
import getYear from 'date-fns/get_year';
import getMonth from 'date-fns/get_month';
import isPast from 'date-fns/is_past';
import { formatDate, monthToString, currencyFormatter } from '../../../common/utils/helpers';
import { installmentStatus } from '../../../common/enums';
import { MajakIcon, HorizontalSelect } from '../../../common/components';
import {
  selectIsDebtor,
  selectIsCreditor
} from '../../../features/auth/ducks';
import { connect } from 'react-redux';
const Bigjs = require('big.js');

const mapStateToProps = state => ({
  isDebtor: selectIsDebtor(state),
  isCreditor: selectIsCreditor(state)
});

@translate()
@connect(mapStateToProps, null)
export default class CalendarPanel extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    installments: PropTypes.array.isRequired,
    firstUnpaidInstallment: PropTypes.object,
    renderSmall: PropTypes.bool,
    isCreditor: PropTypes.bool,
    isDebtor: PropTypes.bool,
    unpaidAmount: PropTypes.number
  };

  constructor(props) {
    super(props);

    const { firstUnpaidInstallment, installments } = this.props;

    const { paymentDue } = firstUnpaidInstallment
      ? firstUnpaidInstallment
      : installments[installments.length - 1];

    this.state = {
      currentYear: getYear(paymentDue).toString(),
    };
  }

  onChangeYear = currentYear => {
    this.setState({ currentYear });
  };

  getSelectOptions() {
    const firstYear = getYear(this.props.installments[0].paymentDue);
    const lastYear = getYear(
      this.props.installments[this.props.installments.length - 1].paymentDue
    );
    const numberOfYears = lastYear - firstYear + 1;

    return Array.from(Array(numberOfYears))
      .map((_, idx) => firstYear + idx)
      .map(y => y.toString());
  }

  getVisibleInstallments() {
    const { installments } = this.props;
    const { currentYear } = this.state;

    return installments
      .map((installment, idx) => ({ ...installment, idx: idx + 1 }))
      .filter(installment => getYear(installment.paymentDue).toString() === currentYear);
  }

  render() {
    const { renderSmall, t, isDebtor, isCreditor, unpaidAmount } = this.props;
    const { currentYear } = this.state;

    const selectOptions = this.getSelectOptions();
    const currentYearIndex = selectOptions.indexOf(currentYear);

    return (
      <div className="payment-calendar">
        <div className="calendar-year-select">
          <HorizontalSelect
            data={selectOptions}
            currentIdx={currentYearIndex}
            onChangeYear={this.onChangeYear}
          />
        </div>
        <div className="current-debt">
          {(unpaidAmount && unpaidAmount > 0) ? (
            <span>
              {t("Aktuálny dlh: ")}
              {currencyFormatter(unpaidAmount)}
            </span>
          ) : null}
        </div>
        <div className="calendar-container">
          {this.getVisibleInstallments().map(installment => (
            <CalendarCell installment={installment} renderSmall={renderSmall} t={t} key={installment.id} canSeeDisposalInfo={!(isDebtor || isCreditor)} />
          ))}
        </div>
      </div>
    );
  }
}

const CalendarCell = ({ installment, renderSmall, t, canSeeDisposalInfo }) => {


  const status = installment.status
  const amount = Number(Number(installment.amount).toFixed(2))
  const principalAmount = Number(Number(installment.principalAmount).toFixed(2))
  const interestAmount = Number(Number(installment.interestAmount).toFixed(2))
  const principalUnpaid = Number(Number(installment.principalUnpaid).toFixed(2))
  const interestUnpaid = Number(Number(installment.interestUnpaid).toFixed(2))
  const insuranceAmount = Number(Number(installment.insuranceAmount).toFixed(2))
  const insuranceUnpaid = Number(Number(installment.insuranceUnpaid).toFixed(2))
  const disposedAmount = Number(Number(installment.disposedAmount).toFixed(2))

  const isLate = (status === installmentStatus.UNPAID) && isPast(installment.paymentDue);
  const isAwaiting = (status === installmentStatus.UNPAID) 
    && (principalAmount === principalUnpaid) 
    && (interestAmount === interestUnpaid) 
    && (insuranceAmount === insuranceUnpaid) && !isLate;

  const isPartiallyPaid = (status === installmentStatus.UNPAID) && (amount > Number(Number(principalUnpaid + interestUnpaid + insuranceUnpaid).toFixed(2)));
  const isPaid = (status === installmentStatus.PAID);
  const isDisposed = !!disposedAmount;
  const isFullDispose = (isDisposed && (Bigjs(amount)).eq(Bigjs(disposedAmount)));
  const partiallyPaidAmount = isPartiallyPaid ? Number((amount - principalUnpaid - interestUnpaid - insuranceUnpaid).toFixed(2)) : 0

  const cellClassName = cn(
    renderSmall ? 'calendar-cell-small' : 'calendar-cell',
    {
      'bg-paid': isPaid,
      'bg-late': isLate,
    }
  );

  return (
    <div className={cellClassName}>
      <div className="calendar-cell-title">{monthToString(getMonth(installment.paymentDue))}</div>
      <div className="calendar-cell-details">
        <p>
          <strong>
            {installment.idx}. {t('splátka')}
          </strong>
        </p>
        <p>
          <strong>{formatDate(installment.paymentDue)}</strong>
        </p>
        <p>
          <strong>{currencyFormatter(installment.amount)}</strong>
        </p>
      </div>
      <div className="calendar-cell-footer">
        {isLate && (
          <span>
            <MajakIcon type="omeskanie" className="color-late" />
            {t('v omeškaní')}
          </span>
        )}
        {isAwaiting && (
          <span>
            <MajakIcon type="check-circled" />
            {t('čaká na úhradu')}
          </span>
        )}
        {isPartiallyPaid && (
          <span>
            {isLate && (
              <span>
                {t(`, uhradených ${currencyFormatter(partiallyPaidAmount)}`)}
              </span>
            )}
            {!isLate && partiallyPaidAmount != 0 && (
              <span>
                <MajakIcon type="check-circled" />
                {t(`uhradených ${currencyFormatter(partiallyPaidAmount)}`)}
              </span>
            )}

          </span>
        )}
        {canSeeDisposalInfo && isDisposed && !isFullDispose && (
          <span>
            {`${t('čiastočne odpredaná za ')} ${currencyFormatter(installment.disposedAmount)}`}
          </span>
        )}
        {canSeeDisposalInfo && isDisposed && isFullDispose && (
          <span>
            {`${t('odpredaná za ')} ${currencyFormatter(installment.disposedAmount)}`}
          </span>
        )}
        {canSeeDisposalInfo && isPaid && !isDisposed && (
          <span>
            <MajakIcon type="check-circled" className="color-paid" />
            {t('uhradená')}
          </span>
        )}
        {!canSeeDisposalInfo && isPaid && (
          <span>
            <MajakIcon type="check-circled" className="color-paid" />
            {t('uhradená')}
          </span>
        )}
      </div>
    </div>
  );
};

CalendarCell.propTypes = {
  installment: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/common/components/data-display/Calendar.js